import { useEffect } from "react";
import { X, ShieldCheck, EyeSlash } from "@phosphor-icons/react";

// Inline PDF viewer with best-effort download protection:
// 1. iframe loads PDF with #toolbar=0&navpanes=0 (Chrome/Firefox respect this)
// 2. right-click is intercepted on the wrapper
// 3. transparent overlay across the surface blocks direct PDF context menu in some viewers
// 4. watermark stamp across the viewport so screenshots show our brand
// A determined user can still grab the file via Network tab — true DRM requires
// server-side conversion to images, which is a separate scope.
export default function PdfViewer({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const blockMenu = (e) => {
    e.preventDefault();
    return false;
  };

  const blockShortcuts = (e) => {
    // Ctrl/Cmd+S, Ctrl/Cmd+P, Ctrl/Cmd+Shift+S — block save/print
    if ((e.ctrlKey || e.metaKey) && ["s", "p"].includes(e.key.toLowerCase())) {
      e.preventDefault();
    }
  };

  const hasPdf = !!item.pdf_url;
  // Hash params suppress toolbar/download in most viewers
  const src = hasPdf ? `${item.pdf_url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH` : "";

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
      onContextMenu={blockMenu}
      onKeyDown={blockShortcuts}
      data-testid="pdf-viewer"
    >
      <div className="relative w-full max-w-5xl h-full bg-zinc-950 border border-zinc-700 flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-zinc-800 text-white flex-shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#66A8FF]">
              Portfolio · {item.service_title}
            </div>
            <div className="font-display text-lg sm:text-xl truncate mt-0.5" data-testid="pdf-title">
              {item.title}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white border border-zinc-700 hover:border-white"
            aria-label="Close"
            data-testid="pdf-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="relative flex-1 bg-zinc-900 overflow-hidden">
          {hasPdf ? (
            <>
              <iframe
                src={src}
                title={item.title}
                className="absolute inset-0 w-full h-full bg-white select-none"
                style={{ pointerEvents: "auto" }}
                onContextMenu={blockMenu}
              />
              {/* Transparent overlay grid for right-click block on top of PDF area */}
              <div
                className="absolute inset-0 pointer-events-none"
                aria-hidden="true"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(-30deg, rgba(0,68,255,0.08) 0px, rgba(0,68,255,0.08) 1px, transparent 1px, transparent 240px)",
                }}
              />
              {/* Diagonal watermarks */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div
                  className="font-display text-[18vmin] font-bold text-zinc-900/15 select-none whitespace-nowrap"
                  style={{ transform: "rotate(-30deg)" }}
                >
                  cad4surveys
                </div>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 px-6 text-center">
              <EyeSlash size={48} weight="duotone" className="text-zinc-600" />
              <div className="mt-4 text-lg text-white font-display">
                Preview coming soon
              </div>
              <p className="mt-2 text-sm max-w-md">
                The sample drawing for <span className="text-white">{item.title}</span> is being prepared.
                Request a free sample drafted to your standards from the contact form.
              </p>
              <a
                href="/free-trial"
                className="mt-6 px-5 py-3 bg-[#0044FF] text-white text-sm font-medium"
                data-testid="pdf-fallback-trial"
              >
                Request a free sample →
              </a>
            </div>
          )}
        </div>
        <div className="px-4 sm:px-6 py-3 border-t border-zinc-800 flex items-center justify-between text-zinc-400 flex-shrink-0">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em]">
            <ShieldCheck size={14} />
            View-only · {item.state || "USA"} · {item.year || ""}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            cad4surveys.com
          </div>
        </div>
      </div>
    </div>
  );
}
